import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Star, Quote, CheckCircle2, XCircle } from "lucide-react";
import cooksFleaMarketAsset from "@/assets/cooks-flea-market-case-study.jpeg.asset.json";
import elPortonImg from "@/assets/case-studies/el-porton.png";
import foodBayanaImg from "@/assets/case-studies/food-bayana.webp";
import skylarkGroupImg from "@/assets/case-studies/skylark-group.webp";
import smileParkImg from "@/assets/case-studies/smile-park.webp";
import lithiumBatteryImg from "@/assets/case-studies/lithium-battery.webp";
import mediaMarktImg from "@/assets/case-studies/media-markt.webp";
import xtremeImg from "@/assets/case-studies/xtreme.webp";
import parkhotelImg from "@/assets/case-studies/parkhotel.webp";
import meritumImg from "@/assets/case-studies/meritum.webp";
import mnactecImg from "@/assets/case-studies/mnactec.webp";

const CaseStudies = () => {
  const caseStudies = [
    {
      title: "Serving Up Success: How El Portón Elevated Dining with Service Robotics",
      image: elPortonImg,
      location: "USA",
      industry: "Food & Beverage",
      robots: ["BellaBot", "KettyBot Pro"],
      description: "In Roswell, Georgia, El Portón Mexican Restaurant has been a beloved local destination, known for its sizzling fajitas, inviting atmosphere, and exceptional service. However, behind the scenes, owner Jose Perez faced an increasing challenge within the food and beverage industry: the difficulty of recruiting and retaining reliable staff to uphold their high service standards.",
      solution: "As turnover rates rose and operational demands intensified, Jose sought a solution to support his team without sacrificing the cherished guest experience. This led him to partner with ToDo Robotics, which introduced Pudu Robotics' KettyBot and BellaBot to help El Portón turn the corner in terms of efficiency, morale, and service excellence."
    },
    {
      title: "Revolutionizing Restaurant Service at Skylark Group with BellaBot",
      image: skylarkGroupImg,
      location: "Japan",
      industry: "Food & Beverage",
      robots: ["BellaBot"],
      description: "Skylark Group, a top family restaurant chain in Japan, has transformed its customer service and employee experience by deploying Pudu Robotics' delivery and advertising robot, Bellabot. In an ambitious plan, Skylark introduced 3000 BellaBots across over 2,000 stores in Japan to enhance operational efficiency, alleviate employee workload, and increase customer satisfaction.",
      solution: "An initial survey conducted at eight stores showed that 67% of customers were satisfied with the robots' presence, indicating the popularity of Bellabot's interactive and friendly design."
    },
    {
      title: "FlashBot Revolutionizes Dining Experience at Food Bayana, Malaysia",
      image: foodBayanaImg,
      location: "Malaysia",
      industry: "Food & Beverage",
      robots: ["FlashBot Max"],
      description: "The newly established Food Bayana dining center in Bayan Lepas, Penang, Malaysia, has announced a collaboration with Pudu Robotics and Pentamaster to drive digital transformation within the center.",
      solution: "This partnership signifies a significant step for Food Bayana in integrating automation technology with culinary experiences, aiming to deliver an unprecedented dining experience for customers."
    },
    {
      title: "BellaBot Enhances Efficiency at Smile Park Elderly Care Home",
      image: smileParkImg,
      location: "Japan",
      industry: "Health Care",
      robots: ["BellaBot"],
      description: "At Smile Park Elderly Care Home in Japan, the introduction of BellaBot has effectively addressed the challenges faced by staff in transporting items, significantly improving workflow and service quality.",
      solution: "This innovation not only enhances staff efficiency but also adds an element of fun to the interactions between residents and robots, allowing caregivers to focus more on providing care and improving the overall living experience."
    },
    {
      title: "Tri-Robot Team: Collaborative Delivery & Cleaning in Battery Manufacturing",
      image: lithiumBatteryImg,
      location: "China",
      industry: "Industrial Facility",
      robots: ["PuduBot 2", "PUDU CC1", "PUDU T300"],
      description: "In the era of Industry 4.0, smart manufacturing has become essential for modern factory development. A leading lithium-ion battery manufacturer in China has embraced this trend by implementing innovative solutions from Pudu Robotics.",
      solution: "This multi-robot ecosystem including PuduBot 2, PUDU CC1, and PUDU T300 enhances operational efficiency across various production processes, establishing a new standard for smart manufacturing."
    },
    {
      title: "Media Markt's Robotic Staff: Setting a New Standard for Retail Efficiency",
      image: mediaMarktImg,
      location: "Netherlands",
      industry: "Retail",
      robots: ["BellaBot", "KettyBot Pro"],
      description: "Established in 1979, Media Markt is a German multinational consumer electronics retail chain with over 1,000 stores worldwide and a sales revenue of €21 billion in 2020. With its spacious stores and diverse product range, Media Markt sought to integrate robots to assist customers.",
      solution: "KettyBot's product search functionality effectively met this need, allowing staff to pre-set product and location information, enabling the robot to guide customers throughout the store. The multi-layer trays and advertising screens provided excellent platforms for marketing materials."
    },
    {
      title: "Xtreme & Pudu Robotics Create a New Trend in Sports Entertainment",
      image: xtremeImg,
      location: "Canada",
      industry: "Entertainment and Sports",
      robots: ["KettyBot Pro", "PUDU CC1"],
      description: "Xtreme, a trampoline park in Canada aimed at teens, has introduced Pudu Robotics' KettyBot and PUDU CC1 to enhance operational efficiency and provide a more engaging experience for children.",
      solution: "KettyBot is responsible for delivering food during party events, while PUDU CC1 takes care of cleaning duties within the venue. Both robots have become indispensable and reliable assistants, known for their outstanding efficiency and friendly, interactive features."
    },
    {
      title: "Pudu Robots Enhances Service Efficiency at Parkhotel in Austria",
      image: parkhotelImg,
      location: "Austria",
      industry: "Hospitality",
      robots: ["BellaBot", "KettyBot Pro", "HolaBot", "FlashBot Max", "PUDU CC1"],
      description: "Parkhotel Eisenstadt in Burgenland, Austria, welcomed five special new team members: Pudu Robotics' KettyBot, HolaBot, BellaBot, FlashBot, and the cleaning robot PUDU CC1.",
      solution: "These robots represent the first robotic employees in Burgenland, taking on responsibilities such as floor cleaning, item and food delivery, and serving as welcoming assistants to engage with guests."
    },
    {
      title: "Transforming Workplace Efficiency: Meritum Integrates KettyBot",
      image: meritumImg,
      location: "Poland",
      industry: "Residential and Office Buildings",
      robots: ["KettyBot Pro"],
      description: "Meritum, a Polish company specializing in accounting and payroll services, has introduced Pudu Robotics' KettyBot to revolutionize its reception processes.",
      solution: "This innovative solution greatly enhances operational efficiency and creates a friendly, vibrant workplace atmosphere."
    },
    {
      title: "Interactive Museum Navigation with KettyBot at MNACTEC",
      image: mnactecImg,
      location: "Spain",
      industry: "Public Service",
      robots: ["KettyBot Pro"],
      description: "The National Museum of Science and Technology of Catalonia (MNACTEC) has successfully integrated two KettyBots into its exhibition areas to enhance the visitor experience.",
      solution: "These state-of-the-art guide robots not only assist in navigating the museum but also engage visitors with interactive audiovisual content."
    }
  ];

  return (
    <>
      <SEO 
        title="Case Studies & Reviews — Cooks Flea Market & PUDU SH1 | Neil AI"
        description="See how Cooks Flea Market in Winston-Salem, NC replaced traditional mop & bucket cleaning with the PUDU SH1. Real customer success stories from Neil AI Global Solutions, an authorized PUDU Robotics dealer."
        keywords="PUDU SH1, robotic floor scrubber, commercial floor cleaning robot, mop bucket replacement, automated cleaning solutions, floor scrubber North Carolina, janitorial automation, facility cleaning technology, commercial cleaning robotics, Winston-Salem cleaning solutions, Cooks Flea Market, Neil AI Global Solutions, PUDU Robotics Authorized Dealer"
      />
      <Navigation />
      
      <div 
        className="min-h-screen py-8 px-4"
        style={{
          background: `radial-gradient(900px 600px at 10% 10%, #132552 0%, #0B1736 45%, #060f24 100%)`
        }}
      >
        <main className="max-w-7xl mx-auto">
          <Link to="/">
            <Button variant="ghost" className="mb-6 mt-2">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>

          <div className="border border-[hsl(var(--line))] rounded-[14px] p-4 sm:p-6 md:p-12" style={{ boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)" }}>
            <h1 className="font-orbitron font-bold text-3xl sm:text-4xl md:text-5xl mb-6 leading-tight">
              Case <span className="text-primary">Studies</span>
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground mb-10 md:mb-12 max-w-3xl leading-relaxed">
              PUDU Robotics offers flexible delivery and cleaning solutions that effectively tackle real-world challenges, adding value in the F&B, hospitality, healthcare, entertainment, industrial manufacturing sectors, and others.
            </p>

            {/* FEATURED CASE STUDY — Cooks Flea Market */}
            <section className="mb-12 md:mb-16" aria-labelledby="featured-case-study">
              <div className="flex items-center gap-3 mb-4">
                <Badge className="bg-primary text-primary-foreground font-bold">★ Featured Review</Badge>
                <Badge variant="outline" className="text-xs">Winston-Salem, NC</Badge>
                <Badge variant="secondary" className="text-xs">Commercial Cleaning</Badge>
              </div>

              <Card className="border-[hsl(var(--line))] glass-effect overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-0">
                  <div className="aspect-video lg:aspect-auto overflow-hidden">
                    <img
                      src={cooksFleaMarketAsset.url}
                      alt="Cooks Flea Market in Winston-Salem, NC — PUDU SH1 robotic floor scrubber deployed by Neil AI Global Solutions"
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-1 mb-3 text-primary" aria-label="5 out of 5 stars">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-current" />
                      ))}
                      <span className="ml-2 text-sm font-semibold text-foreground">Owner Success Story</span>
                    </div>

                    <h2 id="featured-case-study" className="font-orbitron font-bold text-xl md:text-2xl lg:text-3xl mb-4 leading-tight">
                      Cooks Flea Market Replaces Traditional Mop &amp; Bucket Cleaning with the <span className="text-primary">PUDU SH1</span>
                    </h2>

                    <div className="mb-4">
                      <p className="text-xs font-semibold text-primary mb-2">Robot Deployed:</p>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded font-semibold">PUDU SH1 Smart Upright Scrubber</span>
                    </div>

                    <div className="relative pl-6 border-l-2 border-primary text-sm md:text-base text-muted-foreground italic leading-relaxed space-y-3">
                      <Quote className="absolute -left-3 -top-2 w-5 h-5 text-primary bg-background" />
                      <p>
                        "I've been in business a long time, and one thing that's never changed is the amount of time and labor it takes to keep a facility clean. Every day we had multiple mop buckets, wet floors, manual scrubbing, and staff spending valuable time on repetitive cleaning tasks.
                      </p>
                      <p>
                        When Neil AI Global Solutions introduced us to the PUDU SH1, I was skeptical at first. But after seeing it in action, it became clear this wasn't just another cleaning machine—it was a smarter way to operate.
                      </p>
                      <p>
                        The SH1 has helped modernize our cleaning process by reducing the need for traditional mop-and-bucket cleaning while delivering a more consistent clean throughout our facility. Instead of employees pushing heavy buckets and spending hours manually scrubbing floors, the SH1 handles the work efficiently and professionally.
                      </p>
                      <p>
                        What impressed me most was how easy it was to operate and how quickly the Neil AI team trained us on the system. If you're still relying on old-school cleaning methods, I highly recommend taking a look at what Neil AI Global Solutions and the PUDU SH1 can do for your business."
                      </p>
                      <p className="not-italic font-semibold text-foreground">— Cooks Flea Market Ownership Team</p>
                    </div>
                  </div>
                </div>

                {/* Challenge / Solution / Results */}
                <div className="grid md:grid-cols-3 gap-0 border-t border-[hsl(var(--line))]">
                  <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-[hsl(var(--line))]">
                    <h3 className="font-orbitron font-bold text-lg mb-4 text-primary">The Challenge</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {[
                        "Multiple mop buckets throughout the facility",
                        "Labor-intensive floor cleaning",
                        "Inconsistent cleaning results",
                        "Staff spending hours on repetitive tasks",
                        "Increased wear and tear on employees",
                        "Higher operational inefficiencies",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-[hsl(var(--line))]">
                    <h3 className="font-orbitron font-bold text-lg mb-4 text-primary">The Solution</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {[
                        "Automated floor scrubbing",
                        "Consistent cleaning performance",
                        "Reduced repetitive labor",
                        "Faster cleaning cycles",
                        "Professional facility appearance",
                        "Easy operation & staff adoption",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 md:p-8">
                    <h3 className="font-orbitron font-bold text-lg mb-4 text-primary">The Results</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {[
                        "Modernized floor care process",
                        "More efficient cleaning workflow",
                        "Improved cleaning consistency",
                        "Better utilization of staff time",
                        "Enhanced operational efficiency",
                        "Higher staff morale & retention",
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="p-6 md:p-8 border-t border-[hsl(var(--line))] bg-primary/5">
                  <h3 className="font-orbitron font-bold text-lg md:text-xl mb-2">Why Businesses Are Making The Switch</h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-4 leading-relaxed">
                    Your employees weren't hired to spend hours pushing mop buckets. They were hired to serve customers, support operations, and help grow your business. The <strong className="text-foreground">PUDU SH1</strong> helps facilities reduce repetitive cleaning tasks, improve floor cleanliness, support existing staff, and modernize maintenance — all without replacing the team.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-extrabold rounded-xl">
                      <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                        Book a Live SH1 Demo
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="glass-effect border-[hsl(var(--line))] hover:bg-white/10 font-bold rounded-xl">
                      <Link to="/sh1">View PUDU SH1 Specs</Link>
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Review schema for SEO */}
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Review",
                    itemReviewed: {
                      "@type": "Product",
                      name: "PUDU SH1 Smart Upright Scrubber",
                      brand: { "@type": "Brand", name: "PUDU Robotics" },
                    },
                    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
                    author: { "@type": "Organization", name: "Cooks Flea Market", address: { "@type": "PostalAddress", addressLocality: "Winston-Salem", addressRegion: "NC", addressCountry: "US" } },
                    publisher: { "@type": "Organization", name: "Neil AI Global Solutions" },
                    reviewBody:
                      "The PUDU SH1 has helped modernize our cleaning process by reducing the need for traditional mop-and-bucket cleaning while delivering a more consistent clean throughout our facility.",
                  }),
                }}
              />
            </section>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {caseStudies.map((study, index) => (
                <Card 
                  key={index}
                  className="border-[hsl(var(--line))] glass-effect overflow-hidden hover:scale-[1.02] transition-all duration-300"
                >
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge variant="secondary" className="text-xs">
                        {study.location}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {study.industry}
                      </Badge>
                    </div>

                    <h3 className="font-orbitron font-bold text-lg mb-3 line-clamp-2">
                      {study.title}
                    </h3>

                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                      {study.description}
                    </p>

                    <div className="mb-4">
                      <p className="text-xs font-semibold text-primary mb-2">Robots Deployed:</p>
                      <div className="flex flex-wrap gap-1">
                        {study.robots.map((robot, idx) => (
                          <span 
                            key={idx}
                            className="text-xs bg-primary/10 text-primary px-2 py-1 rounded"
                          >
                            {robot}
                          </span>
                        ))}
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground italic border-l-2 border-primary pl-3">
                      {study.solution}
                    </p>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center pt-10 md:pt-12 border-t border-[hsl(var(--line))]">
              <h2 className="font-orbitron font-bold text-2xl sm:text-3xl mb-4 leading-tight">
                Ready to Write Your <span className="text-primary">Success Story?</span>
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto leading-relaxed">
                Join hundreds of businesses worldwide transforming their operations with PUDU Robotics and Neil AI automation solutions.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button 
                  asChild
                  size="lg"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 font-extrabold rounded-xl"
                >
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Schedule Your Demo
                  </a>
                </Button>
                <Button 
                  asChild
                  size="lg"
                  variant="outline"
                  className="glass-effect border-[hsl(var(--line))] hover:bg-white/10 font-bold rounded-xl"
                >
                  <Link to="/robots">
                    Explore Our Robots
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <Footer />
    </>
  );
};

export default CaseStudies;
